/**
 * Persistence for the Map Review page's device-local state, split by how long it
 * should last:
 *
 *   preferences  the map's layer checkboxes and the due-soon horizon. Set once
 *                and expected to stick, so they live in localStorage and are
 *                re-applied on the next visit.
 *   analysis     the filter spec and view (active table, paging, sort, drawer)
 *                as a fallback tier. A reload keeps the operator's work; a new
 *                visit starts clean, so these live in sessionStorage.
 *
 * The analysis tier is the FALLBACK, not the only record of the view: a URL that
 * carries `state=1` (`@/lib/mapReviewUrl`) is authoritative for every field it
 * covers, and a page opened with one ignores what is stored here. A bare
 * `/_/review/map` restores from here exactly as it always has — the marker is
 * what decides which tier is read, so the two never conflict. What both tiers
 * decode is validated once, in `@/lib/mapReviewState`.
 *
 * Everything else the store holds — rows, counts, the map selection, the
 * loading/error flags — is derived from a request or is a mode, and is
 * deliberately never stored. The row selection is the exception: it is not
 * stored here because the URL names it (`item`), which is the only place it
 * needs to survive.
 *
 * The module is pure: it imports no Vue and takes its storage as an argument,
 * so it is unit-testable without a browser. Every access is wrapped in
 * try/catch because private mode and a full quota both throw on read and write,
 * and a preference is never worth breaking the page over.
 */

import { LADDER_OFFSET_DEFAULT, clampH3Offset } from "@/lib/h3/ladder";
import {
	DUE_SOON_DEFAULT_DAYS,
	DUE_SOON_MAX_DAYS,
	DUE_SOON_MIN_DAYS,
} from "@/lib/mapReviewBands";
import {
	defaultLayerVisibility,
	type LayerVisibility,
} from "@/lib/mapReviewLayers";
import {
	MAP_REVIEW_ANALYSIS_VERSION,
	isMapReviewTable,
	isNonNegativeInteger,
	isPositiveInteger,
	isSort,
	parseSpec,
} from "@/lib/mapReviewState";
import type { MapReviewSort, MapReviewSpec, MapReviewTable } from "@/type/api";

/** The browser storage the module reads and writes; a `Storage` satisfies it.
 *  Injectable so tests can pass a fake. */
export interface PrefsStorage {
	getItem(key: string): string | null;
	setItem(key: string, value: string): void;
}

export const LAYERS_KEY = "map-review.layers";
export const DUE_SOON_KEY = "map-review.due-soon-days";
export const ANALYSIS_KEY = "map-review.analysis";

/** This visit's filter spec and view, as persisted. */
export interface MapReviewAnalysisState {
	spec: MapReviewSpec;
	activeTable: MapReviewTable;
	page: number;
	limit: number;
	sort: MapReviewSort | null;
	drawerOpen: boolean;
	drawerExpanded: boolean;
}

/**
 * The browser's storage, or null when it is unavailable. Read lazily and inside
 * a try/catch because merely touching `localStorage` can throw (private mode,
 * a sandboxed frame).
 */
function browserStorage(kind: "local" | "session"): PrefsStorage | null {
	try {
		const storage = kind === "local" ? localStorage : sessionStorage;
		return storage ?? null;
	} catch {
		return null;
	}
}

function read(storage: PrefsStorage | null, key: string): string | null {
	if (!storage) return null;
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

function write(storage: PrefsStorage | null, key: string, value: string): void {
	if (!storage) return;
	try {
		storage.setItem(key, value);
	} catch {
		// Quota reached or storage refused: the state stays in memory and the
		// page carries on.
	}
}

// --- view preferences (localStorage) ---------------------------------------

/**
 * The map's layer visibility, merged over the current defaults so a flag added
 * to `LayerVisibility` after the stored state was written still starts at its
 * default rather than reading as undefined. A missing or corrupt value yields
 * the defaults whole.
 */
export function loadLayerVisibility(
	storage: PrefsStorage | null = browserStorage("local"),
): LayerVisibility {
	const base = defaultLayerVisibility();
	const raw = read(storage, LAYERS_KEY);
	if (raw === null) return base;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return base;
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
		return base;
	}
	for (const key of Object.keys(base) as (keyof LayerVisibility)[]) {
		const value = (parsed as Record<string, unknown>)[key];
		if (typeof value === "boolean") base[key] = value;
	}
	return base;
}

export function saveLayerVisibility(
	visibility: LayerVisibility,
	storage: PrefsStorage | null = browserStorage("local"),
): void {
	write(storage, LAYERS_KEY, JSON.stringify(visibility));
}

/**
 * The due-soon horizon in days, clamped to the band module's range. A missing,
 * non-numeric or unparseable stored value falls back to the default.
 */
export function loadDueSoonDays(
	storage: PrefsStorage | null = browserStorage("local"),
): number {
	const raw = read(storage, DUE_SOON_KEY);
	if (raw === null) return DUE_SOON_DEFAULT_DAYS;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return DUE_SOON_DEFAULT_DAYS;
	}
	return clampDueSoonDays(parsed);
}

export function saveDueSoonDays(
	days: number,
	storage: PrefsStorage | null = browserStorage("local"),
): void {
	write(storage, DUE_SOON_KEY, JSON.stringify(clampDueSoonDays(days)));
}

/** Clamp any value to the band module's integer day range; a non-number falls
 *  back to the default. */
export function clampDueSoonDays(value: unknown): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		return DUE_SOON_DEFAULT_DAYS;
	}
	const rounded = Math.round(value);
	return Math.min(DUE_SOON_MAX_DAYS, Math.max(DUE_SOON_MIN_DAYS, rounded));
}

// --- saved H3 layers (localStorage) ----------------------------------------

/**
 * Which saved H3 layers THIS operator has switched off, and the resolution
 * offset they are all drawn at. Visibility is per-user and device-local: a
 * layer's definition belongs to the district's account, but whether one person
 * is looking at it does not, which is why it rides these prefs rather than a
 * column on the layer.
 */
export const H3_LAYERS_KEY = "map-review.h3-layers";

/** The saved H3 layers' device-local state. */
export interface H3LayerPrefs {
	/** The ids of the saved layers switched OFF, in no particular order. */
	hidden: number[];
	/** The H3 resolution offset every saved layer is drawn at, on the lab's
	 * ladder (`clampH3Offset` keeps it there) — one value for all of them, so
	 * the layers stay comparable cell to cell. */
	offset: number;
}

/** Nothing switched off, at the ladder's default rung. */
export function defaultH3LayerPrefs(): H3LayerPrefs {
	return { hidden: [], offset: LADDER_OFFSET_DEFAULT };
}

/**
 * The stored H3 layer state, or the defaults when there is nothing valid to
 * restore. Any structural problem — unparseable JSON, an off list that is not a
 * list of layer ids, an offset that is not a number — discards the WHOLE stored
 * state rather than half-restoring it, the same all-or-nothing rule the stored
 * analysis follows: a half-read layer list would hide layers the operator never
 * hid.
 */
export function loadH3LayerPrefs(
	storage: PrefsStorage | null = browserStorage("local"),
): H3LayerPrefs {
	const raw = read(storage, H3_LAYERS_KEY);
	if (raw === null) return defaultH3LayerPrefs();
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return defaultH3LayerPrefs();
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
		return defaultH3LayerPrefs();
	}
	const stored = parsed as Record<string, unknown>;
	const hidden = stored.hidden;
	if (!Array.isArray(hidden) || !hidden.every(isPositiveInteger)) {
		return defaultH3LayerPrefs();
	}
	const offset = stored.offset;
	if (typeof offset !== "number" || !Number.isFinite(offset)) {
		return defaultH3LayerPrefs();
	}
	return { hidden, offset: clampH3Offset(offset) };
}

/** The offset is clamped on the way out as well as on the way in, so a state
 * the page wrote is always one it can read back. */
export function saveH3LayerPrefs(
	prefs: H3LayerPrefs,
	storage: PrefsStorage | null = browserStorage("local"),
): void {
	write(
		storage,
		H3_LAYERS_KEY,
		JSON.stringify({
			hidden: prefs.hidden,
			offset: clampH3Offset(prefs.offset),
		}),
	);
}

// --- analysis state (sessionStorage) ---------------------------------------

/**
 * This visit's analysis, or null when there is nothing valid to restore. Any
 * structural problem, a version mismatch included, discards the whole stored
 * state rather than restoring a spec that could make the page error on load.
 */
export function loadAnalysis(
	storage: PrefsStorage | null = browserStorage("session"),
): MapReviewAnalysisState | null {
	const raw = read(storage, ANALYSIS_KEY);
	if (raw === null) return null;
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	return parseAnalysis(parsed);
}

export function saveAnalysis(
	state: MapReviewAnalysisState,
	storage: PrefsStorage | null = browserStorage("session"),
): void {
	write(
		storage,
		ANALYSIS_KEY,
		JSON.stringify({ version: MAP_REVIEW_ANALYSIS_VERSION, ...state }),
	);
}

// --- validation ------------------------------------------------------------

function parseAnalysis(value: unknown): MapReviewAnalysisState | null {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return null;
	}
	const stored = value as Record<string, unknown>;
	if (stored.version !== MAP_REVIEW_ANALYSIS_VERSION) return null;
	const spec = parseSpec(stored.spec);
	if (!spec) return null;
	if (!isMapReviewTable(stored.activeTable)) return null;
	if (!isNonNegativeInteger(stored.page)) return null;
	if (!isPositiveInteger(stored.limit)) return null;
	const sort = stored.sort;
	if (sort !== null && !isSort(sort)) return null;
	const drawerOpen = stored.drawerOpen;
	const drawerExpanded = stored.drawerExpanded;
	if (typeof drawerOpen !== "boolean") return null;
	if (typeof drawerExpanded !== "boolean") return null;
	return {
		spec,
		activeTable: stored.activeTable,
		page: stored.page,
		limit: stored.limit,
		sort,
		drawerOpen,
		drawerExpanded,
	};
}
